import { DeviceCheckNotes, FieldProse, NumberedSection, obj, withInlinePlaceholders } from './DossierPrimitives';

/**
 * EU IVDR (Reg 2017/746) Article 29 Summary of Safety and Performance (SSP).
 * Parity: backend/services/dossier/eu_ivdr_ssp_renderer (`ssp_sections_html`).
 * §1 is a structured device/purpose block; §2–§8 are single prose values.
 */
export function IvdrSspRenderer({ dossier }: { dossier: Record<string, unknown> }) {
  const s1 = obj(dossier, 's1_device_and_intended_purpose');
  const s1Fields: [unknown, string][] = [
    [s1['device_identification'], 'Device identification'],
    [s1['intended_purpose'], 'Intended purpose'],
    [s1['what_is_detected_or_measured'], 'What is detected / measured'],
    [s1['intended_user'], 'Intended user'],
    [s1['target_population'], 'Target population'],
    [s1['indications_and_limitations'], 'Indications and limitations'],
  ];
  const present = s1Fields.filter(([v]) => v != null);
  return (
    <div className="space-y-4">
      <DeviceCheckNotes dossier={dossier} />
      <NumberedSection prefix="Section" num={1} title="Device and Intended Purpose">
        {present.length === 0 ? (
          <p className="text-[13.5px] text-muted italic m-0">No content provided.</p>
        ) : (
          present.map(([v, label], i) => (
            <FieldProse key={i} label={`${label}:`} body={v} fallbackHint={label} />
          ))
        )}
      </NumberedSection>
      <ProseValueSection num={2} title="Device Description" value={dossier['s2_device_description']} />
      <ProseValueSection
        num={3}
        title="Previous Generations and Variants"
        value={dossier['s3_previous_generations_and_variants']}
      />
      <ProseValueSection
        num={4}
        title="Reference to Harmonised Standards and Common Specifications"
        value={dossier['s4_reference_to_harmonised_standards_and_cs']}
      />
      <ProseValueSection
        num={5}
        title="Summary of the Performance Evaluation"
        value={dossier['s5_performance_evaluation_summary']}
      />
      <ProseValueSection
        num={6}
        title="Metrological Traceability"
        value={dossier['s6_metrological_traceability']}
      />
      <ProseValueSection
        num={7}
        title="Suggested Profile and Training for Users"
        value={dossier['s7_suggested_profile_and_training_for_users']}
      />
      <ProseValueSection
        num={8}
        title="Residual Risks, Undesirable Effects, Warnings and Precautions"
        value={dossier['s8_residual_risks_undesirable_effects_warnings_precautions']}
      />
    </div>
  );
}

/** A numbered section whose body is a single prose value (IVDR SSP §2–§8). */
export function ProseValueSection({
  num,
  title,
  value,
}: {
  num: number;
  title: string;
  value: unknown;
}) {
  const s = value == null ? '' : String(value);
  return (
    <NumberedSection prefix="Section" num={num} title={title}>
      {s ? (
        <div className="text-[13.5px] leading-[1.6] text-ink">{withInlinePlaceholders(s)}</div>
      ) : (
        <p className="text-[13.5px] text-muted italic m-0">No content provided.</p>
      )}
    </NumberedSection>
  );
}
